import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import {
  findActiveWord,
  splitWordsIntoLines,
  type Transcription,
  type TranscriptionStatus,
} from "../lib/transcription";
import { useAutoFollow } from "../hooks/useAutoFollow";
import { useTranscriptSearch } from "../hooks/useTranscriptSearch";
import { TranscriptErrorState } from "./TranscriptErrorState";
import { TranscriptSegmentCard } from "./TranscriptSegmentCard";

export interface TranscriptPanelProps {
  /** The full transcription. Null while loading or after an error. */
  transcription: Transcription | null;
  /** Lifecycle of the transcript load. Drives loading / error / data rendering. */
  status: TranscriptionStatus;
  /** Called when the user clicks Retry on the error state. Optional — button hidden if not provided. */
  onRetry?: () => void;
  /** Current playback time in **seconds** (we convert to ms for matching). */
  currentTimeSec: number;
  /** Click a word to seek to it. Receives time in seconds. */
  onSeek: (timeSec: number) => void;
}

/**
 * Side panel rendering the transcription, grouped by speaker. Each segment
 * is split into "lines" by sentence-ending punctuation (matching the Figma
 * layout, where each thought has its own leading timestamp).
 *
 * Orchestrator only: header + search bar + scroll container + jump pill.
 * Per-segment markup lives in TranscriptSegmentCard; per-line + word-level
 * highlight logic lives in TranscriptLine.
 */
export function TranscriptPanel({
  transcription,
  status,
  onRetry,
  currentTimeSec,
  onSeek,
}: TranscriptPanelProps) {
  // Round to integer ms before lookup. Word boundaries in the transcription
  // are integer ms; without rounding, currentTimeSec * 1000 can land at
  // e.g. 46339.9999 (float drift) when the user clicked a word starting at
  // 46340, and findActiveWord falls back to the prior word.
  const currentTimeMs = Math.round(currentTimeSec * 1000);
  const rawActive = useMemo(
    () => (transcription ? findActiveWord(transcription, currentTimeMs) : null),
    [transcription, currentTimeMs],
  );

  // Browser seeks are also frame-quantized — clicking a word can land tens
  // (or hundreds) of ms shy of the requested time, snapping to a keyframe.
  // Until the player catches up, we'd display the prior word as active.
  // Override with the clicked word until rawActive reaches or passes it.
  const [pendingClick, setPendingClick] = useState<{
    segmentIndex: number;
    wordIndex: number;
  } | null>(null);

  useEffect(() => {
    if (!pendingClick) return;
    const reached =
      !!rawActive &&
      (rawActive.segmentIndex > pendingClick.segmentIndex ||
        (rawActive.segmentIndex === pendingClick.segmentIndex &&
          rawActive.wordIndex >= pendingClick.wordIndex));
    if (reached) {
      setPendingClick(null);
      return;
    }
    // Failsafe: clear after ~2s if the player never catches up (e.g., the
    // user paused immediately after clicking and the seek under-shot).
    const timer = setTimeout(() => setPendingClick(null), 2000);
    return () => clearTimeout(timer);
  }, [pendingClick, rawActive]);

  const activePosition = pendingClick
    ? {
        segmentIndex: pendingClick.segmentIndex,
        wordIndex: pendingClick.wordIndex,
        state: "active" as const,
      }
    : rawActive;

  // Pre-split each segment's words into sentence-lines once per transcription
  // change. Stable across time updates so the line layout doesn't churn.
  const segmentLines = useMemo(
    () => (transcription ?? []).map((s) => splitWordsIntoLines(s.words)),
    [transcription],
  );

  // Search state + match navigation owned by useTranscriptSearch.
  const {
    searchQuery,
    setSearchQuery,
    isSearching,
    segmentMatches,
    allMatches,
    currentMatchIndex,
    currentMatch,
    currentMatchLocation,
    goNextMatch,
    goPrevMatch,
  } = useTranscriptSearch(transcription, segmentLines);

  // Smart-follow autoscroll for the active playback word. Paused while
  // searching so the search-scroll below doesn't fight it.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    autoFollow,
    jumpDirection,
    jumpToCurrent,
    enableAutoFollow,
    popKey,
  } = useAutoFollow({
    scrollContainerRef,
    activeSelector: "[data-active-line='true']",
    activeKey: activePosition
      ? `${activePosition.segmentIndex}:${activePosition.wordIndex}`
      : null,
    paused: isSearching,
  });

  // Clicking a word is a deliberate navigation — re-engage autofollow so
  // playback keeps scrolling the panel from the new position, even if the
  // user had previously scrolled away.
  const handleWordClick = useCallback(
    (segmentIndex: number, wordIndex: number, timeSec: number) => {
      setPendingClick({ segmentIndex, wordIndex });
      // Snap, don't smooth-scroll — animating the panel while the active
      // word is also changing (pendingClick → seek → rawActive catches up)
      // reads as jitter.
      enableAutoFollow({ instant: true });
      onSeek(timeSec);
    },
    [onSeek, enableAutoFollow],
  );

  // Search-driven scroll. Debounced ~80ms so typing doesn't thrash but
  // prev/next nav still feels instant.
  useEffect(() => {
    if (!isSearching) return;
    const handle = setTimeout(() => {
      const container = scrollContainerRef.current;
      const match = container?.querySelector(
        "[data-current-match='true']",
      ) as HTMLElement | null;
      if (!container || !match) return;
      const containerRect = container.getBoundingClientRect();
      const lineRect = match.getBoundingClientRect();
      const targetTopOffset = containerRect.height / 3;
      const delta = lineRect.top - containerRect.top - targetTopOffset;
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: "smooth",
      });
    }, 80);
    return () => clearTimeout(handle);
  }, [isSearching, currentMatchLocation, currentMatchIndex]);

  const handleDownload = () => {
    if (!transcription) return;
    const blob = new Blob([JSON.stringify(transcription, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transcription.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <aside className="relative flex h-full w-80 shrink-0 flex-col border-r border-[#DFE1E4] bg-white text-[#282A30]">
      <header className="flex items-center justify-between border-b border-[#DFE1E4] px-4 py-3">
        <h2 className="text-sm font-medium text-[#282A30]">Transcription</h2>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!transcription}
          title="Download transcription JSON"
          className="rounded p-1 text-[#71717A] transition-colors hover:bg-[#F7F8F9] hover:text-[#282A30] disabled:opacity-40"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
        </button>
      </header>

      <div className="border-b border-[#DFE1E4] px-4 py-3">
        <div className="relative flex h-8 items-center rounded-md border border-[#DFE1E4] bg-white pr-1.5 pl-2 shadow-[0_1px_4px_-2px_rgba(0,0,0,0.16)] transition-colors focus-within:border-[#A28AFB]">
          <MagnifyingGlassIcon className="pointer-events-none mr-1.5 h-4 w-4 shrink-0 text-[#90959D]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && searchQuery) {
                e.preventDefault();
                setSearchQuery("");
                return;
              }
              if (e.key === "Enter" && allMatches.length > 0) {
                e.preventDefault();
                if (e.shiftKey) goPrevMatch();
                else goNextMatch();
              }
            }}
            placeholder="Search transcription..."
            className="min-w-0 flex-1 bg-transparent text-xs leading-[15px] text-[#474C57] placeholder-[#90959D] focus:outline-none"
          />
          {isSearching && (
            <>
              <span className="ml-1 shrink-0 select-none text-[11px] tabular-nums text-[#71717A]">
                {allMatches.length === 0
                  ? "0/0"
                  : `${currentMatchIndex + 1}/${allMatches.length}`}
              </span>
              <button
                type="button"
                onClick={goPrevMatch}
                disabled={allMatches.length === 0}
                title="Previous match (Shift+Enter)"
                className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#71717A] transition-colors hover:bg-[#F7F8F9] hover:text-[#282A30] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronUpIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={goNextMatch}
                disabled={allMatches.length === 0}
                title="Next match (Enter)"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#71717A] transition-colors hover:bg-[#F7F8F9] hover:text-[#282A30] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
              <div className="mx-1 h-4 w-px shrink-0 bg-[#DFE1E4]" />
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#90959D] transition-colors hover:bg-[#F7F8F9] hover:text-[#282A30]"
                title="Clear search (Esc)"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-3 pt-3 pb-24">
        {status === "error" ? (
          <TranscriptErrorState onRetry={onRetry} />
        ) : transcription === null ? (
          <p className="px-2 text-xs text-[#71717A]">Loading transcription…</p>
        ) : transcription.length === 0 ? (
          <p className="px-2 text-xs text-[#71717A]">
            No transcription available.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {transcription.map((segment, segmentIndex) => (
              <TranscriptSegmentCard
                key={segmentIndex}
                segment={segment}
                segmentIndex={segmentIndex}
                lines={segmentLines[segmentIndex]}
                activePosition={activePosition}
                isSearching={isSearching}
                segmentMatchInfo={segmentMatches?.[segmentIndex]}
                currentMatch={currentMatch}
                currentMatchLocation={currentMatchLocation}
                popKey={popKey}
                onWordClick={handleWordClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Centering wrapper — framer-motion animates transform on the
          button, which would clobber a Tailwind -translate-x-1/2. */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
        <AnimatePresence>
          {!autoFollow && activePosition && (
            <motion.button
              key="jump-to-current"
              type="button"
              onClick={jumpToCurrent}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="pointer-events-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-xs font-medium text-white shadow-lg transition-colors hover:bg-gray-700"
            >
              {jumpDirection === "up" ? (
                <>
                  <ArrowUpIcon className="h-3.5 w-3.5" />
                  Jump to current
                </>
              ) : jumpDirection === "down" ? (
                <>
                  <ArrowDownIcon className="h-3.5 w-3.5" />
                  Jump to current
                </>
              ) : (
                // Active word is still in view but autoscroll is paused —
                // clicking re-engages so playback keeps scrolling into view.
                "Resume autoscroll"
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </aside>
  );
}
